import React, { useEffect, useState } from 'react';
import { Box, Button, InputAdornment, TextField, Typography } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';

import { formatDuration } from './logCall';
import { checkRecordingUrl, RECORDING_URL_MAX } from './recording';
import { RecordingPlayer } from './RecordingPlayer';

/** Typing pauses this long before the link is checked and tried. */
const SETTLE_MS = 400;
/** A link that has not answered by then is said to be slow; it can still be saved. */
const SLOW_MS = 12_000;

export interface RecordingFieldProps {
  value: string;
  onChange: (text: string) => void;
  /** A refusal to show: the form's own check on submit, or the API's. */
  error?: string;
  disabled?: boolean;
  /** Offered when the call has no length yet: "Use 3:12 as the length". */
  onUseDuration?: (seconds: number) => void;
}

type Probe = { state: 'checking' } | { state: 'slow' } | { state: 'plays'; seconds: number | null } | { state: 'fails'; message: string };

/**
 * The recording link (card 21.7). Checked as the API checks it once typing
 * settles; then the player tries it in this browser, fetching only enough to
 * know it plays and how long it is. A link that does not play here is a
 * warning, not a refusal: it may need a sign-in other admins have.
 */
export const RecordingField = ({ value, onChange, error, disabled, onUseDuration }: RecordingFieldProps) => {
  const [settled, setSettled] = useState(value);
  const [probe, setProbe] = useState<Probe>({ state: 'checking' });

  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), SETTLE_MS);
    return () => clearTimeout(timer);
  }, [value]);

  const check = checkRecordingUrl(settled);
  const url = check.url ?? null;

  useEffect(() => {
    if (!url) return undefined;
    setProbe({ state: 'checking' });
    const timer = setTimeout(() => setProbe((was) => (was.state === 'checking' ? { state: 'slow' } : was)), SLOW_MS);
    return () => clearTimeout(timer);
  }, [url]);

  // A link still being typed is not judged yet; the form's submit check catches it then.
  const typing = value !== settled;
  const problem = error ?? (!typing ? check.problem : undefined);

  let status: React.ReactNode = 'Optional. A link to the audio file: https, from your phone system or file store.';
  if (url && !problem && !typing) {
    if (probe.state === 'checking') status = 'Checking the link…';
    else if (probe.state === 'slow') status = 'No answer from the link yet. It can still be saved.';
    else if (probe.state === 'fails') status = `${probe.message} It can still be saved.`;
    else status = probe.seconds ? `Plays · ${formatDuration(probe.seconds)} long` : 'Plays';
  }
  const warn = probe.state === 'fails' || probe.state === 'slow';

  return (
    <Box sx={{ display: 'grid', gap: 1 }}>
      <TextField
        label="Recording link"
        size="small"
        type="url"
        placeholder="https://recordings.example.org/call.mp3"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onBlur={() => setSettled(value)}
        disabled={disabled}
        error={Boolean(problem)}
        helperText={
          <Box component="span" sx={{ color: !problem && url && warn && !typing ? 'warning.dark' : undefined }}>
            {problem ?? status}
          </Box>
        }
        inputProps={{ maxLength: RECORDING_URL_MAX + 100, inputMode: 'url', spellCheck: false, autoComplete: 'off' }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LinkIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            </InputAdornment>
          ),
          endAdornment: value ? (
            <InputAdornment position="end">
              <Button size="small" onClick={() => onChange('')} disabled={disabled} aria-label="Remove the recording link" sx={{ minWidth: 0 }}>
                Remove
              </Button>
            </InputAdornment>
          ) : undefined,
        }}
      />
      {url && !problem && (
        <Box>
          <RecordingPlayer
            key={url}
            url={url}
            preload="metadata"
            quietProblems
            onDuration={(seconds) => setProbe({ state: 'plays', seconds })}
            onProblem={(message) => setProbe(message ? { state: 'fails', message } : (was) => (was.state === 'plays' ? was : { state: 'plays', seconds: null }))}
          />
          {probe.state === 'plays' && probe.seconds && onUseDuration && (
            <Typography variant="caption" component="p" sx={{ mt: 0.5 }}>
              <Button size="small" onClick={() => onUseDuration(Math.round(probe.seconds as number))} sx={{ px: 0.5, minWidth: 0 }}>
                Use {formatDuration(probe.seconds)} as the call's length
              </Button>
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};
